import React, { useState, useRef, useEffect } from "react";
import { Calendar, ChevronDown } from "lucide-react";

const PRESETS = [
  { label: "Hôm nay", key: "today" },
  { label: "Hôm qua", key: "yesterday" },
  { label: "Tuần này", key: "this_week" },
  { label: "Tháng này", key: "this_month" },
  { label: "30 ngày", key: "last_30" },
  { label: "Tháng trước", key: "last_month" },
];

function getPresetDates(key) {
  const now = new Date();
  const fmt = (d) => d.toISOString().slice(0, 10);
  const today = fmt(now);

  switch (key) {
    case "today": return { start: today, end: today };
    case "yesterday": {
      const y = new Date(now); y.setDate(y.getDate() - 1);
      return { start: fmt(y), end: fmt(y) };
    }
    case "this_week": {
      const d = now.getDay() || 7;
      const mon = new Date(now); mon.setDate(now.getDate() - d + 1);
      return { start: fmt(mon), end: today };
    }
    case "this_month": {
      return { start: `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-01`, end: today };
    }
    case "last_30": {
      const d30 = new Date(now); d30.setDate(d30.getDate() - 30);
      return { start: fmt(d30), end: today };
    }
    case "last_month": {
      const pm = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const pmEnd = new Date(now.getFullYear(), now.getMonth(), 0);
      return { start: fmt(pm), end: fmt(pmEnd) };
    }
    default: return { start: today, end: today };
  }
}

export function DateRangePicker({ start, end, onChangeStart, onChangeEnd, className = "" }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const applyPreset = (key) => {
    const { start: s, end: e } = getPresetDates(key);
    onChangeStart(s);
    onChangeEnd(e);
    setOpen(false);
  };

  const fmtDisplay = (d) => {
    if (!d) return "—";
    const parts = d.split("-");
    return `${parts[2]}/${parts[1]}/${parts[0]}`;
  };

  return (
    <div className={`relative ${className}`} ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1.5 bg-[#1e293b] border border-[#334155] rounded-lg px-2.5 py-1.5 text-[10px] text-slate-300 hover:border-indigo-500/40 transition-colors"
        data-testid="date-range-picker"
      >
        <Calendar className="w-3.5 h-3.5 text-indigo-400" />
        <span>{fmtDisplay(start)}</span>
        <span className="text-slate-600">→</span>
        <span>{fmtDisplay(end)}</span>
        <ChevronDown className="w-3 h-3 text-slate-500" />
      </button>

      {open && (
        <div className="absolute top-full left-0 mt-1 z-50 bg-[#1e293b] border border-[#334155] rounded-xl shadow-xl p-3 w-[320px]" data-testid="date-range-dropdown">
          {/* Quick presets */}
          <div className="grid grid-cols-3 gap-1.5 mb-3">
            {PRESETS.map(p => (
              <button key={p.key} onClick={() => applyPreset(p.key)} className="px-2 py-1.5 text-[10px] bg-[#0f172a] border border-[#334155] rounded-md text-slate-300 hover:border-indigo-500/40 hover:text-indigo-400 transition-colors">
                {p.label}
              </button>
            ))}
          </div>

          {/* Custom range */}
          <div className="border-t border-[#334155] pt-2">
            <p className="text-[9px] text-slate-500 uppercase mb-1.5">Tùy chọn khoảng thời gian</p>
            <div className="flex items-center gap-2">
              <input
                type="date"
                value={start}
                onChange={e => onChangeStart(e.target.value)}
                className="flex-1 bg-[#0f172a] border border-[#334155] rounded-md px-2 py-1.5 text-[10px] text-slate-200"
              />
              <span className="text-slate-600 text-xs">→</span>
              <input
                type="date"
                value={end}
                onChange={e => onChangeEnd(e.target.value)}
                className="flex-1 bg-[#0f172a] border border-[#334155] rounded-md px-2 py-1.5 text-[10px] text-slate-200"
              />
            </div>
            <button onClick={() => setOpen(false)} className="w-full mt-2 bg-indigo-500 hover:bg-indigo-400 text-white rounded-md py-1.5 text-[10px] font-semibold">
              Áp dụng
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
